import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import { type IClock } from '@/services/Clock';
import { type ILocalStorage } from '@/services/LocalStorage';
import { type EpochDay, MS_PER_DAY } from '@/domain/beforered';

const STORAGE_KEY = 'beforered:settings';
const DEFAULT_HORIZON = 180;
const DEFAULT_PURCHASE_LEAD = 14;
const DEFAULT_PRODUCTION_LEAD = 1;
const DEFAULT_SAFETY_STOCK = 0;
const MIN_HORIZON = 7;
const MAX_HORIZON = 730;

interface Persisted {
  horizonDays: number;
  defaultPurchaseLeadDays: number;
  defaultProductionLeadDays: number;
  safetyStockPercent: number;
}

export class SettingsStore {
  private _horizonDays: number = DEFAULT_HORIZON;
  private _defaultPurchaseLeadDays: number = DEFAULT_PURCHASE_LEAD;
  private _defaultProductionLeadDays: number = DEFAULT_PRODUCTION_LEAD;
  private _safetyStockPercent: number = DEFAULT_SAFETY_STOCK;

  constructor(
    private clock: IClock,
    private storage: ILocalStorage,
  ) {
    makeAutoObservable(this);
    this.hydrate();
  }

  get horizonDays(): number {
    return this._horizonDays;
  }

  get defaultPurchaseLeadDays(): number {
    return this._defaultPurchaseLeadDays;
  }

  get defaultProductionLeadDays(): number {
    return this._defaultProductionLeadDays;
  }

  get safetyStockPercent(): number {
    return this._safetyStockPercent;
  }

  get today(): EpochDay {
    return Math.floor(this.clock.now() / MS_PER_DAY);
  }

  setHorizonDays = (value: number): void => {
    const clamped = Math.max(MIN_HORIZON, Math.min(MAX_HORIZON, Math.floor(value)));
    this._horizonDays = clamped;
    this.persist();
  };

  setDefaultPurchaseLeadDays = (value: number): void => {
    this._defaultPurchaseLeadDays = Math.max(0, Math.floor(value));
    this.persist();
  };

  setDefaultProductionLeadDays = (value: number): void => {
    this._defaultProductionLeadDays = Math.max(0, Math.floor(value));
    this.persist();
  };

  setSafetyStockPercent = (value: number): void => {
    const clamped = Math.max(0, Math.min(1, value));
    this._safetyStockPercent = clamped;
    this.persist();
  };

  private hydrate(): void {
    const raw = this.storage.get(STORAGE_KEY);
    if (!raw) return;
    try {
      const p = JSON.parse(raw) as Partial<Persisted>;
      // Route through setters: a tampered payload still gets clamped.
      if (typeof p.horizonDays === 'number') this.setHorizonDays(p.horizonDays);
      if (typeof p.defaultPurchaseLeadDays === 'number')
        this.setDefaultPurchaseLeadDays(p.defaultPurchaseLeadDays);
      if (typeof p.defaultProductionLeadDays === 'number')
        this.setDefaultProductionLeadDays(p.defaultProductionLeadDays);
      if (typeof p.safetyStockPercent === 'number') this.setSafetyStockPercent(p.safetyStockPercent);
    } catch {
      /* defaults */
    }
  }

  private persist(): void {
    const p: Persisted = {
      horizonDays: this._horizonDays,
      defaultPurchaseLeadDays: this._defaultPurchaseLeadDays,
      defaultProductionLeadDays: this._defaultProductionLeadDays,
      safetyStockPercent: this._safetyStockPercent,
    };
    this.storage.set(STORAGE_KEY, JSON.stringify(p));
  }
}

export const TOKEN_SettingsStore = token<SettingsStore>('SettingsStore');
